import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../hooks/useSession';
import { KodeniCredit } from '../components/KodeniCredit';

export default function LoginPage() {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [providers, setProviders] = useState<{ google: boolean; facebook: boolean } | null>(null);
  const [params] = useSearchParams();
  const setUser = useSession(s => s.setUser);
  const nav = useNavigate();

  const queryError = params.get('error');
  const queryMsg = params.get('msg');

  useEffect(() => {
    api.providers().then(setProviders).catch(() => setProviders({ google: false, facebook: false }));
  }, []);

  async function guest(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const u = await api.guestLogin(name.trim());
      setUser(u);
      // A pending invite (from /g/:code) is redeemed centrally in App once
      // the session lands — that path also covers OAuth sign-ins.
      nav('/');
    } catch (e: any) {
      setErr(e.message || 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  const hasSso = providers?.google || providers?.facebook;

  return (
    <div
      className="min-h-full flex items-center justify-center px-4"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div className="panel p-8 max-w-sm w-full relative overflow-hidden">
        {/* Decorative cards behind the title */}
        <div className="pointer-events-none absolute -top-6 -right-4 opacity-90" aria-hidden>
          <div className="absolute left-0 top-0 w-14 h-20 rounded-lg bg-gradient-to-br from-white to-ink-100 rotate-12 shadow-card flex items-center justify-center text-suit-red font-display font-bold text-lg">A♥</div>
          <div className="absolute left-6 top-3 w-14 h-20 rounded-lg bg-gradient-to-br from-white to-ink-100 rotate-[26deg] shadow-card flex items-center justify-center text-ink-950 font-display font-bold text-lg">K♠</div>
        </div>
        <div className="text-center mb-6">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-300 to-accent-600 text-ink-950 items-center justify-center text-3xl shadow-glow mb-3 font-display font-bold">♠</div>
          <h1 className="font-display text-3xl font-bold tracking-wide text-accent">DURAK</h1>
          <p className="text-ink-200 text-sm mt-1">Play the classic with friends — anywhere.</p>
        </div>

        {providers?.google && (
          <a href="/auth/google" className="btn-primary w-full mb-3">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
              <path fill="#fff" d="M44.5 20H24v8.5h11.8C34.7 33 30 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.9 2.9l6-6C34 5.4 29.3 3.5 24 3.5 12.7 3.5 3.5 12.7 3.5 24S12.7 44.5 24 44.5c11 0 20.2-8 20.2-20.5 0-1.4-.2-2.7-.5-4z"/>
            </svg>
            Continue with Google
          </a>
        )}
        {providers?.facebook && (
          <a href="/auth/facebook" className="btn-secondary w-full mb-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v6.9A10 10 0 0 0 22 12z"/></svg>
            Continue with Facebook
          </a>
        )}

        {hasSso && <div className="text-center text-xs text-ink-300 my-3">or play as guest</div>}
        <form onSubmit={guest} className="space-y-3">
          <input
            className="input"
            placeholder="Your name"
            maxLength={24}
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <button className="btn-secondary w-full" disabled={busy || !name.trim()}>
            {busy ? 'Signing in…' : 'Play as guest'}
          </button>
        </form>
        {err && <div className="mt-3 text-rose-400 text-sm">{err}</div>}
        {queryError === 'google' && (
          <div className="mt-3 text-rose-400 text-sm">
            Google sign-in failed{queryMsg ? `: ${queryMsg}` : ''}. Try guest mode.
          </div>
        )}
        {queryError === 'fb' && (
          <div className="mt-3 text-rose-400 text-sm">
            Facebook sign-in failed{queryMsg ? `: ${queryMsg}` : ''}. Try guest mode.
          </div>
        )}
        <p className="text-[11px] text-ink-300 mt-6 text-center">
          By signing in you agree to our{' '}
          <a href="/terms" className="text-accent underline">Terms</a> and{' '}
          <a href="/privacy" className="text-accent underline">Privacy Policy</a>.
        </p>
        <div className="mt-4 flex justify-center">
          <KodeniCredit />
        </div>
      </div>
    </div>
  );
}
